// src/context/ApiProvider.tsx
import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import fetchWithToken from '../utils/api';
import { getToken } from '../tokenUtils/tokenUtils';
import { ApiContextType } from '../types';

const ApiContext = createContext<ApiContextType | undefined>(undefined);

export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, _setToken] = useState<string | null>(null);

  // Only commit state when the value actually changes
  const setTokenIfChanged = useCallback((next: string | null) => {
    _setToken(prev => (prev === next ? prev : next));
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      const t = await getToken();
      if (alive) setTokenIfChanged(t);
    })();

    // keep tabs in sync
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'api_token') setTokenIfChanged(e.newValue);
    };
    window.addEventListener('storage', onStorage);

    return () => {
      alive = false;
      window.removeEventListener('storage', onStorage);
    };
  }, [setTokenIfChanged]);

  // Stable function identity
  const refreshToken = useCallback(async () => {
    const t = await getToken(true);
    setTokenIfChanged(t);
    return t;
  }, [setTokenIfChanged]);

  // Stable provider value (prevents child effects keyed to the whole context from firing every render)
  const ctxValue = useMemo<ApiContextType>(() => {
    return { fetchWithToken, token, refreshToken };
  }, [token, refreshToken]);

  return <ApiContext.Provider value={ctxValue}>{children}</ApiContext.Provider>;
};

export const useApi = (): ApiContextType => {
  const ctx = useContext(ApiContext);
  if (!ctx) throw new Error('useApi must be used within an ApiProvider');
  return ctx;
};
